import { OrderEndpoint } from '../common/endpoints';
import { PaymentTypeStr } from '../common/enums';
import { Order, OrderHistoryItem, OrderSummary, PaginatedData, ServerResponse, StaffOrder } from '../common/types';
import { getRequest, patchRequest, postRequest } from './requests';

export interface PaginatedHistoryParams {
  startDate: Date;
  endDate: Date;
  skip: number;
  take: number;
  hidePaid: boolean;
  customerName: string
}


type UpdateOrderStation = {
  orderId: number;
  newStationId: number;
  productCategories: string[];
};

type ChangeQueueOrderOrderItem = {
  id: number;
  queueOrder: number | null;
};

type ChangeQueueOrderOrder = {
  id: number;
  orderItems: ChangeQueueOrderOrderItem[];
};

export type ChangeQueueOrders = {
  orderDtos: ChangeQueueOrderOrder[];
  productCategories: string[];
};

type StartStopFinishOrderOrderItem = {
  id: number;
  status: string;
};

export type GetOrdersParams = {
  includeHistory?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
};

type StartStopFinishOrder = {
  id: number;
  startTime: Date | null;
  finishTime: Date | null;
  paymentType: string | null;
  softDelete: boolean;
  orderItems: StartStopFinishOrderOrderItem[];
};

async function create(order: Order) {
  return await postRequest(OrderEndpoint, order);
}
async function markAsPaid(id: number, paymentType: PaymentTypeStr) {
  return await postRequest(`${OrderEndpoint}/${id}/mark-as-paid/${paymentType}`, null);
}

async function getOrderHistory(params: PaginatedHistoryParams) {
  const queryParams = new URLSearchParams();
  queryParams.append('startDate', params.startDate.toISOString());
  queryParams.append('endDate', params.endDate.toISOString());
  queryParams.append('skip', params.skip.toString());
  queryParams.append('take', params.take.toString());
  queryParams.append('hidePaid', params.hidePaid ? '1' : '0');
  if (params.customerName) {
    queryParams.append('customerName', params.customerName);
  }
  const result: ServerResponse<PaginatedData<OrderHistoryItem>> = await getRequest(
    `${OrderEndpoint}/history?${queryParams.toString()}`,
  );
  return result;
}

async function getOrders() {
  const result: ServerResponse<StaffOrder[]> = await getRequest(OrderEndpoint);
  return result;
}

async function startStopFinishOrder(payload: StartStopFinishOrder) {
  const result: ServerResponse<null> = await patchRequest(
    `${OrderEndpoint}/${payload.id}/startstopfinish`,
    payload,
  );
  return result;
}

async function changeQueueOrders(payload: ChangeQueueOrders) {
  const result: ServerResponse<null> = await patchRequest(
    `${OrderEndpoint}/queues`,
    payload,
  );
  return result;
}

async function updateStation(payload: UpdateOrderStation) {
  const result: ServerResponse<null> = await patchRequest(
    `${OrderEndpoint}/${payload.orderId}/station`,
    payload,
  );
  return result;
}

async function getById(id: number) {
  const result: ServerResponse<OrderSummary> = await getRequest(
    `${OrderEndpoint}/${id}`,
  );
  return result;
}

async function sendReceipt(id: number, email: string) {
  const result: ServerResponse<StaffOrder> = await postRequest(`${OrderEndpoint}/${id}/receipt`, { email })
  return result
}

async function removeOrder(id: number) {
  const result: ServerResponse<StaffOrder> = await postRequest(`${OrderEndpoint}/${id}/remove`, null)
  return result
}

export default {
  create,
  getOrders,
  getOrderHistory,
  getById,
  startStopFinishOrder,
  changeQueueOrders,
  updateStation,
  sendReceipt,
  markAsPaid,
  removeOrder
};
